import { FC, useEffect, useState } from 'react';
import { IListCheckEntry } from 'src/ts/interfaces/listCheck';
import { isResolved, trendAgreeEdits, trendAgreed } from '../../helpers/listCheckHelpers';
import {
  ListCheckAnswered,
  ListCheckDetailFoot,
  ListCheckFootSpacer,
  ListCheckSecondaryButton,
  ListCheckSelectButton
} from '../../styles';
import DisagreeModal from './DisagreeModal';
import { listCheckCopy } from './listCheckCopy';

interface FraudTrendFooterProps {
  entry: IListCheckEntry;
  isSubmitting: boolean;
  canAnswer: boolean;
  onAnswer: (edited: boolean, note?: string) => void;
  onUndo: () => void;
}

const ruledLabel = (edited: boolean) => (edited ? listCheckCopy.edited : listCheckCopy.notEdited);

const pickedText = (entry: IListCheckEntry) => {
  const edited = entry.ruling === 'blocked';
  const agreed = trendAgreed(entry.outcome, edited);
  if (agreed === null) return listCheckCopy.youRuled(ruledLabel(edited));
  return agreed
    ? listCheckCopy.youAgreed(ruledLabel(edited))
    : listCheckCopy.youDisagreed(ruledLabel(edited));
};

const FraudTrendFooter: FC<FraudTrendFooterProps> = ({
  entry,
  isSubmitting,
  canAnswer,
  onAnswer,
  onUndo
}) => {
  const [disagreeing, setDisagreeing] = useState(false);
  const agreeEdits = trendAgreeEdits(entry.outcome);
  const resolved = isResolved(entry);
  useEffect(() => {
    if (resolved) setDisagreeing(false);
  }, [resolved]);

  if (entry.ruling === 'not_run') return null;

  if (resolved) {
    return (
      <ListCheckDetailFoot data-testid="advanced-fraud-trends-foot">
        <ListCheckAnswered data-testid="advanced-fraud-trends-picked">
          {pickedText(entry)}
        </ListCheckAnswered>
        <ListCheckFootSpacer />
        <ListCheckSecondaryButton
          type="button"
          id="advanced-fraud-trends-undo"
          data-testid="advanced-fraud-trends-undo"
          disabled={isSubmitting}
          onClick={onUndo}
        >
          {listCheckCopy.undo}
        </ListCheckSecondaryButton>
      </ListCheckDetailFoot>
    );
  }

  if (!canAnswer) return null;

  if (agreeEdits === null) {
    return (
      <ListCheckDetailFoot data-testid="advanced-fraud-trends-foot">
        <ListCheckFootSpacer />
        <ListCheckSecondaryButton
          type="button"
          id="advanced-fraud-trends-not-edited"
          data-testid="advanced-fraud-trends-not-edited"
          disabled={isSubmitting}
          onClick={() => onAnswer(false)}
        >
          {listCheckCopy.notEdited}
        </ListCheckSecondaryButton>
        <ListCheckSelectButton
          type="button"
          id="advanced-fraud-trends-edited"
          data-testid="advanced-fraud-trends-edited"
          disabled={isSubmitting}
          onClick={() => onAnswer(true)}
        >
          {listCheckCopy.edited}
        </ListCheckSelectButton>
      </ListCheckDetailFoot>
    );
  }

  return (
    <ListCheckDetailFoot data-testid="advanced-fraud-trends-foot">
      <ListCheckFootSpacer />
      <ListCheckSecondaryButton
        type="button"
        id="advanced-fraud-trends-disagree"
        data-testid="advanced-fraud-trends-disagree"
        disabled={isSubmitting}
        onClick={() => setDisagreeing(true)}
      >
        {listCheckCopy.disagree}
      </ListCheckSecondaryButton>
      <ListCheckSelectButton
        type="button"
        id="advanced-fraud-trends-agree"
        data-testid="advanced-fraud-trends-agree"
        disabled={isSubmitting}
        onClick={() => onAnswer(agreeEdits)}
      >
        {listCheckCopy.agree}
      </ListCheckSelectButton>
      {disagreeing && (
        <DisagreeModal
          ruled={ruledLabel(!agreeEdits)}
          isLoading={isSubmitting}
          onConfirm={(note) => onAnswer(!agreeEdits, note)}
          onCancel={() => setDisagreeing(false)}
        />
      )}
    </ListCheckDetailFoot>
  );
};

export default FraudTrendFooter;
